import {
  BaseService,
  Status,
  DataResponse,
  MessageResponse,
  MessageData,
} from '@/services/base.service';
import { Wiki, WikiPage } from '@/models/wiki.model';
import { File as FileItem } from '@/models/file.model';

class WikiService extends BaseService {
  private url: string = '/api/wiki';
  private fileUrl: string = '/api/file';

  private cached: WikiPage[] = [];

  constructor() {
    super();
  }

  public async getList(): Promise<DataResponse<WikiPage[]>> {
    let res: any = {
      status: Status.Ok,
      data: [],
    };
    if (!this.hasCache()) {
      res = await this.$http.get<WikiPage[]>(`${this.url}/list`);
      this.setCache(res.data);
    }
    return {
      status: res.status === Status.Ok,
      data: this.getCache(),
    };
  }

  public async get(id: number): Promise<DataResponse<Wiki>> {
    const list = this.getCache();
    const page = list.find((p) => p.id === id);

    if (page) {
      return {
        status: true,
        data: { page, changes: [] },
      };
    }

    const res = await this.$http.get<Wiki>(
      id ? `${this.url}/${id}` : `${this.url}`,
    );
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async getBySlug(slug: string): Promise<DataResponse<Wiki>> {
    const list = this.getCache();
    const page = list.find((p) => p.slug === slug);

    if (page) {
      return {
        status: true,
        data: { page, changes: [] },
      };
    }

    const res = await this.$http.get<Wiki>(
      slug ? `${this.url}/slug/${slug}` : `${this.url}`,
    );
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async put(message: WikiPage): Promise<DataResponse<WikiPage>> {
    const res = await this.$http.put<WikiPage>(
      `${this.url}/${message.id}`,
      message,
    );
    this.clearCache();
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async getFiles(): Promise<DataResponse<FileItem[]>> {
    const res = await this.$http.get<FileItem[]>(`${this.fileUrl}`);

    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async putFile(file: File): Promise<DataResponse<FileItem>> {
    const formData = new FormData();
    formData.set('file', file);
    formData.set('filename', file.name);

    const res = await this.$http.post<FileItem>(`${this.fileUrl}`, formData);

    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async post(message: WikiPage): Promise<DataResponse<WikiPage>> {
    const res = await this.$http.post<WikiPage>(this.url, message);
    this.clearCache();
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async delete(id: number): Promise<MessageResponse> {
    const res = await this.$http.delete<MessageData>(`${this.url}/${id}`);
    this.clearCache();
    return {
      status: res.status === Status.Ok,
      message: res.data.message,
    };
  }

  private getCache(): WikiPage[] {
    return this.cached;
  }

  private setCache(list: WikiPage[]): void {
    this.cached = list;
  }

  private hasCache(): boolean {
    return this.cached.length !== 0;
  }

  private clearCache(): void {
    this.setCache([]);
  }
}

export default new WikiService();
